import React, { useState } from "react";
import {
  Box,
  Chip,
  Divider,
  Grid,
  IconButton,
  Paper,
  Tooltip,
  Typography,
} from "@mui/material";
import ArrowDropUpIcon from "@mui/icons-material/ArrowDropUp";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import moment from "moment";
import {
  getInitials,
  getTwoLetterInitials,
  hexToRgba,
} from "../../../utils/util";
import PickTask from "../../../assets/images/Pick Task.svg";
import DropTask from "../../../assets/images/Drop Task.svg";
import V2DueDateDialog from "../../Instructions/components/Dashboard/Components/V2DueDateDialog";
import DueDateDialog from "../../Instructions/components/DueDateDialog";

const TaskCardDashboard = ({
  task,
  isExpanded,
  subTasks,
  handleRedirect,
  handleRedirectSubTask,
  handleSubTaskOpen,
  handleCompleteTask,
  handleTaskAction,
  checkDate,
  getInitialsFromFullName,
  colourHex,
  getAllTasks, // Function to refresh tasks after due date update
  subTasksMap, // Map of subtasks needed for V2DueDateDialog
}) => {
  // Local state for due date dialogs
  const [selectedDueDateData, setSelectedDueDateData] = useState(null);
  const [isOpenDueDate, setOpenDueDate] = useState({
    v1: false,
    v2: false,
  });

  // Local state to track updated due dates
  const [updatedDueDates, setUpdatedDueDates] = useState({});

  // Function to update due date of main task
  const handleUpdateDueDate = async (data, e) => {
    e.stopPropagation();
    if (data?.taskType === "Instruction2") {
      setSelectedDueDateData({
        id: data?.id,
        dueDate: data?.dueDate,
        taskType: data?.taskType,
        taskId: data?.id,
        isSubTask: data?.isSubTask,
        name: data?.name,
      });
      setOpenDueDate((prev) => ({ ...prev, v2: true }));
    } else {
      setSelectedDueDateData({
        id: data?.id,
        dueDate: data?.dueDate,
        taskType: data?.taskType,
      });
      setOpenDueDate((prev) => ({ ...prev, v1: true }));
    }
  };

  // Function to update due date of subtask
  const handleUpdateSubTaskDueDate = async (data, taskId, e) => {
    e?.stopPropagation();
    if (data?.taskType === "Instruction2") {
      setSelectedDueDateData({
        id: data?.id,
        dueDate: data?.dueDate,
        taskType: data?.taskType,
        taskId: taskId ? taskId : null,
        isSubTask: data?.isSubTask,
      });
      setOpenDueDate((prev) => ({ ...prev, v2: true }));
    } else {
      setSelectedDueDateData({
        id: data?.id,
        dueDate: data?.dueDate,
        taskType: data?.taskType,
      });
      setOpenDueDate((prev) => ({ ...prev, v1: true }));
    }
  };

  // Enhanced close handlers that update local state immediately
  const handleCloseV1Dialog = (updatedData) => {
    setOpenDueDate((prev) => ({ ...prev, v1: false }));

    // If due date was updated, store it locally for immediate display
    if (updatedData?.id && updatedData?.dueDate) {
      setUpdatedDueDates((prev) => ({
        ...prev,
        [updatedData.id]: updatedData.dueDate,
      }));
    }

    // Refresh data in background
    if (getAllTasks) {
      getAllTasks();
    }
  };

  const handleCloseV2Dialog = (updatedData) => {
    setOpenDueDate((prev) => ({ ...prev, v2: false }));

    // If due date was updated, store it locally for immediate display
    if (updatedData?.id && updatedData?.dueDate) {
      setUpdatedDueDates((prev) => ({
        ...prev,
        [updatedData.id]: updatedData.dueDate,
      }));
    }

    // Refresh data in background
    if (getAllTasks) {
      getAllTasks();
    }
  };

  // Helper function to get the current due date (updated or original)
  const getCurrentDueDate = (taskData) => {
    return updatedDueDates[taskData?.id] || taskData?.dueDate;
  };

  const productCircleStyles = {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: "24px",
    height: "24px",
    fontSize: "12px",
    color: "white",
    fontWeight: "500",
    backgroundColor: "grey",
    textAlign: "center",
    borderRadius: "50%",
    marginLeft: "10px",
  };

  const circleStyles = {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: "28px",
    height: "28px",
    fontSize: "12px",
    color: "#3d4740",
    fontWeight: "500",
    backgroundColor: "#e5faeb",
    textAlign: "center",
    borderRadius: "50%",
    margin: "6px",
    marginRight: "3px",
  };

  return (
    <>
      {/* V2 Dialog for update due date */}
      {isOpenDueDate?.v2 && (
        <V2DueDateDialog
          open={isOpenDueDate}
          handleClose={handleCloseV2Dialog}
          selectedData={selectedDueDateData}
          isV2Dashboard={true}
          getAllTasks={getAllTasks}
          subTasks={subTasks}
          subTasksMap={subTasksMap}
        />
      )}

      {/* Old Dialog for update due date */}
      {isOpenDueDate.v1 && (
        <DueDateDialog
          isIndividual={
            selectedDueDateData?.taskType === "Instance" ||
            selectedDueDateData?.taskType === "Custom"
          }
          open={isOpenDueDate}
          handleClose={handleCloseV1Dialog}
          selectedData={selectedDueDateData}
          getV2Tasks={getAllTasks}
          isClientTask={selectedDueDateData?.taskType === "Client" && true}
          isV2Dashboard={true}
        />
      )}

      <div
        key={task?.id + task?.taskType}
        onClick={() => handleRedirect(task)}
        style={{
          cursor: "pointer",
          margin: 2,
          marginBottom: 4,
        }}
      >
        <Grid item xs={12} display="flex" flexDirection="row">
          {/* Task Details */}
          <Grid item xs={2} mt={2}>
            {/* Case name */}
            {task?.instruction?.InstructionLeadClient?.length > 0 && (
              <Typography className="normal_normal_600_12_Manrope ">
                {task.instruction.InstructionLeadClient.slice(0, 2)
                  .map((item) => {
                    const lead = item?.LeadClient;
                    return `${lead?.firstName} ${lead?.lastName}`;
                  })
                  .join(" & ")}
              </Typography>
            )}

            {/* task Type */}
            <Typography className="normal_normal_12_Manrope dark_grey">
              {task?.taskType === "Instance"
                ? "Duplicate"
                : task?.taskType || "--"}
            </Typography>
            {/* plan product name */}
            <Typography className="normal_normal_12_Manrope dark_grey">
              {!task?.planProduct || task.planProduct === ":"
                ? "--"
                : task.planProduct}
            </Typography>
          </Grid>

          {/* Middle divider */}
          <Grid item xs={0.5} display={"flex"}>
            <Divider
              variant="middle"
              sx={{
                border: "1px solid #DCDCE1",
                marginTop: "10px",
                marginBottom: "10px",
              }}
            />
          </Grid>

          {/* Metadata + Outcome */}
          <Grid item xs={10} mt={2} display="flex" flexDirection="column">
            <Grid item display="flex">
              {/* actor */}
              <Grid item xs={3}>
                <Typography className="dark_grey normal_normal_600_12_Manrope_Update_Milestone ">
                  ACTOR
                </Typography>
                <Typography className="normal_normal_600_12_Manrope green">
                  {task?.actor ||
                    task?.assingnedToRole ||
                    task?.assignedToUserRole ||
                    " -- "}
                </Typography>
              </Grid>
              {/* Due Date */}
              <Grid item xs={4}>
                <Typography className="normal_normal_600_12_Manrope_Update_Milestone dark_grey">
                  DUE BY
                </Typography>

                <Typography
                  className={`normal_normal_600_12_Manrope_dueDate ${
                    checkDate(task) ? "red" : "green"
                  }`}
                  style={{
                    cursor: "pointer",
                    width: "11em",
                  }}
                  onClick={(e) => handleUpdateDueDate(task, e)}
                >
                  {getCurrentDueDate(task)
                    ? moment(getCurrentDueDate(task)).format(
                        "DD/MM/YYYY hh:mm A"
                      )
                    : "--"}
                </Typography>
              </Grid>
            </Grid>

            {/* Task Title and Toggle */}
            <Grid
              xs={12}
              container
              justifyContent="space-between"
              alignItems="center"
            >
              {/* Left: Expand Icon, Initials, Action Buttons, Task Name */}
              <Grid
                container
                xs={6}
                alignItems="center"
                wrap="nowrap"
                gap={0.6}
              >
                {/* Expand/Collapse Icon */}
                <Grid item>
                  <IconButton
                    onClick={(e) => handleSubTaskOpen(task, e)}
                    sx={{
                      // visibility:
                      //   !task?.isSubTask &&
                      //   task.isMainTaskHasSubtasks === true &&
                      //   task?.apiVersionId == null &&
                      //   task?.taskType === "Instruction2"
                      //     ? "visible"
                      //     : "hidden",
                      visibility: "hidden",
                    }}
                  >
                    <ArrowDropUpIcon
                      fontSize="small"
                      sx={{
                        transform: isExpanded
                          ? "rotate(180deg)"
                          : "rotate(0deg)",
                        transition: "transform 0.3s ease",
                        color: isExpanded ? "#00ce3f" : "inherit",
                        cursor: "pointer",
                      }}
                    />
                  </IconButton>
                </Grid>

                {/* Initial letters of picked by */}
                <Grid item sx={{ minWidth: 37 }}>
                  {task?.assignedToUserDetails?.trim() ? (
                    <Box style={circleStyles}>
                      <Tooltip
                        title={
                          task?.assignedToUserDetails?.trim()
                            ? task.assignedToUserDetails
                            : "NA"
                        }
                      >
                        <span>
                          {task?.assignedToUserDetails?.trim()
                            ? getInitialsFromFullName(
                                task.assignedToUserDetails
                              )
                            : "NA"}
                        </span>
                      </Tooltip>
                    </Box>
                  ) : (
                    task?.modifiedBy &&
                    (() => {
                      let initials = "";
                      let tooltipText = "";

                      const first = task?.modifiedBy?.firstName;
                      const last = task?.modifiedBy?.lastName;
                      const hasName = first || last;

                      if (!hasName) return null;

                      initials = getInitials(first, last);
                      tooltipText = `${first} ${last}`;

                      return (
                        <div style={circleStyles}>
                          <Tooltip title={tooltipText}>
                            <span>{initials || "AC"}</span>
                          </Tooltip>
                        </div>
                      );
                    })()
                  )}
                </Grid>

                {/* Pick Task Button */}
                <Grid
                  item
                  style={{
                    display:
                      task?.isPickedButton &&
                      task?.status !== "Picked" &&
                      task?.status !== "Completed"
                        ? "block"
                        : "none",
                  }}
                >
                  <IconButton
                    onClick={(e) =>
                      handleTaskAction(
                        task,
                        null,
                        {
                          status: "Picked",
                        },
                        e
                      )
                    }
                  >
                    <Box
                      sx={{
                        backgroundColor: "#00ce3f",
                        borderRadius: "20px",
                        height: 35,
                        width: 35,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <img
                        src={PickTask}
                        alt="Pick Task"
                        style={{ height: 18, width: 18 }}
                      />
                    </Box>
                  </IconButton>
                </Grid>

                {/* Drop Task Button */}
                <Grid
                  item
                  style={{ display: task?.isDroppedButton ? "block" : "none" }}
                >
                  <IconButton
                    onClick={(e) =>
                      handleTaskAction(
                        task,
                        null,
                        {
                          status: "Dropped",
                        },
                        e
                      )
                    }
                  >
                    <Box
                      sx={{
                        backgroundColor: "red",
                        borderRadius: "20px",
                        height: 35,
                        width: 35,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <img
                        src={DropTask}
                        alt="Drop Task"
                        style={{ height: 18, width: 18 }}
                      />
                    </Box>
                  </IconButton>
                </Grid>

                {/* Task Name */}
                <Grid item display={"flex"}>
                  {/* Name */}
                  <Typography
                    className="normal_normal_600_12_Manrope_DropDown_Menu "
                    style={{
                      color:
                        task?.taskType === "Instruction2"
                          ? colourHex
                          : "#00ce3f",
                    }}
                  >
                    {task?.name || "--"}
                  </Typography>
                  {/* Product name */}
                  {task?.productName ? (
                    <Tooltip title={task?.productName || ""}>
                      <div style={productCircleStyles}>
                        {getTwoLetterInitials(task?.productName || "") || "NA"}
                      </div>
                    </Tooltip>
                  ) : (
                    ""
                  )}
                </Grid>
              </Grid>

              {/* Right: Outcomes or Complete Button */}
              <Grid
                container
                xs={6}
                style={{ padding: 1 }}
                justifyContent={"flex-end"}
              >
                {["Instruction", "Custom", "Client", "Instance"].includes(
                  task?.taskType
                )
                  ? task?.isCompleteButton && (
                      <Tooltip title="Click to complete task">
                        <IconButton
                          onClick={(e) =>
                            handleCompleteTask(
                              task,
                              null,
                              {
                                status: "Completed",
                              },
                              e
                            )
                          }
                        >
                          <CheckCircleIcon sx={{ color: "#dee2e6" }} />
                        </IconButton>
                      </Tooltip>
                    )
                  : task?.NewTaskBranching?.NewTaskBranchingOutCome?.map(
                      (outcome) => (
                        <Chip
                          key={outcome?.Outcome?.id}
                          className="normal_normal_12_Manropee"
                          label={outcome?.Outcome?.name || "NA"}
                          style={{
                            marginLeft: 4,
                            marginTop: 4,
                            border: `2px solid ${outcome?.Outcome?.colourHex}`,
                            backgroundColor: "transparent",
                            color: outcome?.Outcome?.colourHex,
                            cursor: outcome?.Outcome?.isCompleteButton
                              ? "pointer"
                              : "not-allowed",
                          }}
                          onClick={
                            outcome?.Outcome?.isCompleteButton
                              ? (e) =>
                                  handleCompleteTask(
                                    task,
                                    null,
                                    {
                                      outcomeId: outcome?.Outcome?.id,
                                      status: "Completed",
                                    },
                                    e
                                  )
                              : undefined
                          }
                        />
                      )
                    )}
              </Grid>
            </Grid>

            {/* ------------------Subtasks------------------------- */}
            {isExpanded &&
              subTasks?.map((subTask, index) => {
                const lastIndex = index === subTasks.length - 1;
                return (
                  <>
                    {/* main subTask container */}
                    <Grid
                      key={subTask?.id}
                      container
                      xs={12}
                      display={"flex"}
                      alignItems={"center"}
                      onClick={() => handleRedirectSubTask(subTask)}
                    >
                      {/* Actor + Due by + Completed on */}
                      <Grid
                        item
                        xs={12}
                        display="flex"
                        style={{
                          padding: 0.5,
                        }}
                        gap={5}
                      >
                        <Grid item display={"flex"} flexDirection={"column"}>
                          <Typography className="normal_normal_600_12_Manrope_Update_Milestone">
                            Actor
                          </Typography>
                          <Typography className="normal_normal_600_12_Manrope green">
                            {subTask?.actor ||
                              subTask?.assingnedToRole ||
                              subTask?.assignedToUserRole ||
                              " -- "}
                          </Typography>
                        </Grid>

                        <Grid item display={"flex"} flexDirection={"column"}>
                          <Typography className="normal_normal_600_12_Manrope_Update_Milestone">
                            DUE BY
                          </Typography>
                          <Tooltip
                            title={
                              subTask?.isCompleted
                                ? "Task is completed you can not update date"
                                : "Click here to update due date"
                            }
                          >
                            <Typography
                              className={`normal_normal_600_12_Manrope_dueDate ${
                                checkDate(subTask) ? "red" : "green"
                              }`}
                              style={{ cursor: "pointer" }}
                              onClick={(e) =>
                                handleUpdateSubTaskDueDate(subTask, task?.id, e)
                              }
                            >
                              {getCurrentDueDate(subTask)
                                ? moment(getCurrentDueDate(subTask)).format(
                                    "DD/MM/YYYY hh:mm A"
                                  )
                                : "--"}
                            </Typography>
                          </Tooltip>
                        </Grid>

                        <Grid item display={"flex"} flexDirection={"column"}>
                          <Typography className="normal_normal_600_12_Manrope_Update_Milestone">
                            COMPLETED ON
                          </Typography>
                          <Typography className="normal_normal_600_12_Manrope green">
                            {subTask?.endDate
                              ? moment(subTask?.endDate).format(
                                  "DD/MM/YYYY hh:mm A"
                                )
                              : "--"}
                          </Typography>
                        </Grid>
                      </Grid>
                      {/* subTask name + initial letters(product+compledtedBy) + outcomes */}
                      <Grid
                        xs={12}
                        container
                        padding={0.5}
                        alignItems={"center"}
                        ml={5.3}
                      >
                        <Grid
                          item
                          xs={6}
                          display={"flex"}
                          alignItems={"center"}
                          gap={1}
                        >
                          {/* completedBy/modifiedBy */}
                          <Grid item minWidth={37}>
                            {(() => {
                              let initials = "";
                              let tooltipText = "";

                              if (subTask?.isCompleted) {
                                const first =
                                  subTask?.delegatedModifiedBy?.firstName;
                                const last =
                                  subTask?.delegatedModifiedBy?.lastName;
                                const hasName = first || last;
                                initials = getInitials(first, last);
                                tooltipText = hasName
                                  ? ` ${first || ""} ${last || ""}`.trim()
                                  : "Auto Completed";
                              } else {
                                const first = subTask?.modifiedBy?.firstName;
                                const last = subTask?.modifiedBy?.lastName;
                                const hasName = first || last;

                                if (!hasName) return null;

                                initials = getInitials(first, last);
                                tooltipText = subTask?.isTaskPickedByhim
                                  ? `${first} ${last}`
                                  : `${first} ${last}`;
                              }

                              return (
                                <div style={circleStyles}>
                                  <Tooltip title={tooltipText}>
                                    <span>{initials || "AC"}</span>
                                  </Tooltip>
                                </div>
                              );
                            })()}
                          </Grid>
                          <Grid item minWidth={25}>
                            {(() => {
                              // Case 1: Completed task
                              if (
                                subTask?.status === "Completed" &&
                                subTask?.isCompleted
                              ) {
                                return (
                                  <Tooltip title="Completed task">
                                    <CheckCircleIcon
                                      sx={{
                                        color: "#00CE3F",
                                      }}
                                    />
                                  </Tooltip>
                                );
                              }

                              // Case 2: Pick Task button
                              if (
                                subTask?.isPickedButton &&
                                subTask?.status !== "Picked" &&
                                subTask?.status !== "Completed"
                              ) {
                                return (
                                  <Box
                                    sx={{
                                      backgroundColor: "#00ce3f",
                                      borderRadius: "20px",
                                      height: 35,
                                      width: 35,
                                      display: "flex",
                                      alignItems: "center",
                                      justifyContent: "center",
                                    }}
                                    onClick={(e) =>
                                      handleTaskAction(
                                        subTask,
                                        task,
                                        {
                                          status: "Picked",
                                        },
                                        e
                                      )
                                    }
                                  >
                                    <img
                                      src={PickTask}
                                      alt="Pick Task"
                                      style={{
                                        height: 18,
                                        width: 18,
                                      }}
                                    />
                                  </Box>
                                );
                              }

                              // Case 3: Drop Task button
                              if (subTask?.isDroppedButton) {
                                return (
                                  <Box
                                    sx={{
                                      backgroundColor: "red",
                                      borderRadius: "20px",
                                      height: 32,
                                      width: 32,
                                      display: "flex",
                                      alignItems: "center",
                                      justifyContent: "center",
                                    }}
                                    onClick={(e) =>
                                      handleTaskAction(
                                        subTask,
                                        task,
                                        {
                                          status: "Dropped",
                                        },
                                        e
                                      )
                                    }
                                  >
                                    <img
                                      src={DropTask}
                                      alt="Drop Task"
                                      style={{
                                        height: 18,
                                        width: 18,
                                      }}
                                    />
                                  </Box>
                                );
                              }
                            })()}
                          </Grid>
                          {/* SubTask name + Product Name */}
                          <Grid item display={"flex"}>
                            {/* Name */}
                            <Typography
                              className="normal_normal_600_12_Manrope_DropDown_Menu"
                              style={{
                                color: hexToRgba(colourHex, 0.7) || "#00ce3f",
                              }}
                            >
                              {subTask?.name}
                            </Typography>
                            {/* Product name */}
                            {subTask?.instructionProduct?.product
                              ?.productName ? (
                              <Tooltip
                                title={
                                  subTask?.instructionProduct?.product
                                    ?.productName || ""
                                }
                              >
                                <div style={productCircleStyles}>
                                  {getTwoLetterInitials(
                                    subTask?.instructionProduct?.product
                                      ?.productName || ""
                                  ) || "NA"}
                                </div>
                              </Tooltip>
                            ) : (
                              ""
                            )}
                          </Grid>
                        </Grid>

                        <Grid
                          container
                          xs={6}
                          justifyContent={"flex-end"}
                          padding={0.5}
                        >
                          {subTask?.NewTaskBranching?.NewTaskBranchingOutCome?.filter(
                            (outcome) => {
                              const selectedId = subTask?.Outcome?.id;

                              if (
                                !subTask?.isCompleted &&
                                subTask?.status !== "Completed"
                              ) {
                                // Not completed → show all
                                return true;
                              }

                              // Completed → show only selected
                              return (
                                selectedId &&
                                selectedId === outcome?.Outcome?.id
                              );
                            }
                          )?.map((outcome) => {
                            const isSelected =
                              subTask?.Outcome?.id === outcome?.Outcome?.id;
                            const outcomeColor =
                              outcome?.Outcome?.colourHex ||
                              outcome?.colourHex ||
                              "grey";

                            const isCompleted =
                              subTask?.isCompleted ||
                              subTask?.status === "Completed";

                            const textColor =
                              isCompleted && isSelected
                                ? "white"
                                : subTask?.isCompleted === false
                                ? outcomeColor
                                : outcomeColor;
                            const bgColor =
                              isCompleted && isSelected
                                ? "grey"
                                : !subTask?.isCompleted
                                ? "white"
                                : "white";
                            const borderColor = isSelected
                              ? "grey"
                              : outcomeColor;

                            return (
                              <Chip
                                key={outcome?.Outcome?.id}
                                className="normal_normal_600_12_Manrope"
                                label={
                                  <span
                                    style={{
                                      color: textColor,
                                      letterSpacing: "1px",
                                    }}
                                  >
                                    {outcome?.Outcome?.name || "NA"}
                                  </span>
                                }
                                sx={{
                                  border: `2px solid ${borderColor}`,
                                  backgroundColor: bgColor,
                                  cursor:
                                    !isCompleted &&
                                    outcome?.Outcome?.isCompleteButton
                                      ? "pointer"
                                      : "not-allowed",
                                  opacity: isCompleted && isSelected ? 0.7 : 1,
                                }}
                                onClick={
                                  !isCompleted &&
                                  outcome?.Outcome?.isCompleteButton
                                    ? (e) =>
                                        handleCompleteTask(
                                          subTask,
                                          task,
                                          {
                                            outcomeId: outcome?.Outcome?.id,
                                            status: "Completed",
                                          },
                                          e
                                        )
                                    : undefined
                                }
                              />
                            );
                          })}
                        </Grid>
                      </Grid>
                    </Grid>
                    <Grid item xs={12} m={0} p={0}>
                      {!lastIndex && (
                        <Divider
                          orientation="horizontal"
                          sx={{ border: "1.5px solid #DCDCE1" }}
                        />
                      )}
                    </Grid>
                  </>
                );
              })}
          </Grid>
        </Grid>

        <Divider
          orientation="horizontal"
          sx={{ border: "1.5px solid #DCDCE1" }}
        />
      </div>
    </>
  );
};

export default TaskCardDashboard;
